import useToaster from "./useToaster";

export default function useHighlights() {
  const { addToaster } = useToaster();
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const url = `${baseUrl}/api/highlights`;

  const safeFetch = async (endpoint, options = {}) => {
    try {
      const token = localStorage.getItem("x-token");
      const headers = {};
      if (!(options.body instanceof FormData))
        headers["Content-Type"] = "application/json";
      headers["Authorization"] = `Bearer ${token}`;
      if (options.headers) Object.assign(headers, options.headers);

      const response = await fetch(endpoint, { ...options, headers });

      if (response.status === 401) {
        // App's auth-logout handler clears the session and flips to <Login>.
        window.dispatchEvent(new Event("auth-logout"));
        // Early-return: a 401 body is empty, so calling response.json() below
        // throws a cryptic "Unexpected end of JSON input". Bail with a clean
        // message and let the redirect take over.
        throw new Error("Session expired — please log in again");
      }
      // Parse defensively: a 502/504 from the proxy returns HTML (not JSON), so
      // response.json() would throw and mask the real status. Surface status.
      let data = null;
      try {
        data = await response.json();
      } catch {
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
      }
      if (!response.ok) {
        throw new Error(data?.message || `Request failed (${response.status})`);
      }
      return data;
    } catch (error) {
      addToaster({
        message: `${error?.message || JSON.stringify(error)}`,
        severity: "error",
      });
      throw error;
    }
  };

  const listHighlights = ({
    status,
    tab,
    shows = [],
    from,
    to,
    search,
    page = 1,
    pageSize = 20,
  } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (tab) qs.set("tab", tab);
    (shows || []).forEach((s) => qs.append("shows", s));
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    if (search) qs.set("search", search);
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    return safeFetch(`${url}?${qs.toString()}`);
  };

  const getHighlight = (id) => safeFetch(`${url}/${id}`);

  const listRuns = (limit = 30) => safeFetch(`${url}/runs?limit=${limit}`);

  const pickHighlight = (id) =>
    safeFetch(`${url}/${id}/pick`, { method: "POST" });

  const unpickHighlight = (id) =>
    safeFetch(`${url}/${id}/unpick`, { method: "POST" });

  // Admin-only retry of a failed generation (first-time generation starts on pick).
  const generateHighlight = (id) =>
    safeFetch(`${url}/${id}/generate`, { method: "POST" });

  const chooseHighlightTitle = (id, choice) =>
    safeFetch(`${url}/${id}/title`, {
      method: "POST",
      body: JSON.stringify({ choice }),
    });

  // Combine a suggested same-story pair into one candidate (server concatenates
  // the clips). Returns { highlight } — the new merged candidate.
  const mergeHighlight = (id, withId) =>
    safeFetch(`${url}/${id}/merge`, {
      method: "POST",
      body: JSON.stringify({ withId }),
    });

  return {
    listHighlights,
    listRuns,
    getHighlight,
    pickHighlight,
    unpickHighlight,
    generateHighlight,
    chooseHighlightTitle,
    mergeHighlight,
  };
}
